import { memo, useCallback } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSearchParams } from 'react-router-dom';

import type { SlowSection } from '@/types';
import type { PickingInfo } from '@deck.gl/core';

import getSlowSectionDeckLayers from '@/components/map/slow-section-marker/slow-section-deck-layers';
import DeckGLOverlay from '@/components/map/train-marker/deckgl-overlay';
import useGetSlowSections from '@/hooks/use-get-slow-sections';
import { useMapConfig } from '@/hooks/use-map-config';
import usePinnedItem from '@/hooks/use-pinned-item';
import { useZoomThreshold } from '@/hooks/use-zoom-threshold';
import { SLOW_SECTION_LAYER, ZOOM_11 } from '@/utils/constants/map';
import {
  SEARCH_PARAM_DRAWER,
  SEARCH_PARAM_LOCK_PIN,
  SEARCH_PARAM_LOCK_PIN_TYPE,
  SEARCH_PARAM_SECTION_ID,
  SEARCH_PARAM_SHOW,
  SEARCH_PARAM_SLOW_SECTION_ID,
  SEARCH_PARAM_VIEW,
} from '@/utils/constants/queries';
import { useDeckCursorHover } from '@/utils/helpers/train-simulation/use-deck-cursor-hover';

const SlowSectionMarker = () => {
  const { config } = useMapConfig();
  const { current: mapRef } = useMap();

  const isLarge = useZoomThreshold(mapRef, ZOOM_11);
  const [, setSearchParams] = useSearchParams();
  const { data: slowSections = [] } = useGetSlowSections();

  const { pinnedItemId, pinnedItemType } = usePinnedItem();

  const handleHover = useDeckCursorHover(mapRef);
  const isVisible = Boolean(config.slowSections);

  const handleSlowSectionClick = useCallback(
    (info: PickingInfo) => {
      if (!mapRef || !info.object) {
        return;
      }

      const targetSection = info.object as SlowSection;

      if (info.layer?.id?.startsWith(SLOW_SECTION_LAYER)) {
        setSearchParams((prev) => {
          if (pinnedItemId) {
            prev.set(SEARCH_PARAM_LOCK_PIN, pinnedItemId);
            prev.set(SEARCH_PARAM_DRAWER, 'true');
          }

          if (pinnedItemType) {
            prev.set(SEARCH_PARAM_LOCK_PIN_TYPE, pinnedItemType);
          }

          prev.set(SEARCH_PARAM_SLOW_SECTION_ID, targetSection.id);
          prev.delete(SEARCH_PARAM_SHOW);
          prev.delete(SEARCH_PARAM_DRAWER);
          prev.delete(SEARCH_PARAM_VIEW);
          prev.delete(SEARCH_PARAM_SECTION_ID);

          return prev;
        });
      }
    },
    [mapRef, pinnedItemId, pinnedItemType]
  );

  const slowSectionLayers = getSlowSectionDeckLayers(
    slowSections,
    handleSlowSectionClick,
    isVisible,
    isLarge
  );

  return <DeckGLOverlay layers={slowSectionLayers} onHover={handleHover} />;
};

export default memo(SlowSectionMarker);
